import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Check,
  ExternalLink,
  Key,
  Lock,
  Server,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { auth } from "../lib/auth";

const CATEGORY_TONE = {
  AI: "text-emerald-300 border-emerald-500/40",
  "SMS / 2FA": "text-sky-300 border-sky-500/40",
  Payments: "text-amber-300 border-amber-500/40",
  Email: "text-purple-300 border-purple-500/40",
};

function KeyRow({ k }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const save = useMutation({
    mutationFn: (value) =>
      auth.post("/owner/keys/set", { key: k.key, value }).then((r) => r.data),
    onSuccess: (d) => {
      setEditing(false);
      setDraft("");
      qc.invalidateQueries({ queryKey: ["owner-keys"] });
      toast.success(
        d?.cleared ? `${k.label}: cleared` : `${k.label}: saved`,
      );
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const tone = CATEGORY_TONE[k.category] || "text-zinc-300 border-zinc-700";
  const dot = k.configured ? "bg-emerald-400" : "bg-zinc-700";
  const statusLabel = k.configured
    ? k.source === "db"
      ? "CONFIGURED · DB"
      : "CONFIGURED · ENV"
    : k.required
    ? "MISSING · REQUIRED"
    : "OPTIONAL · MOCK MODE";

  return (
    <div
      data-testid={`owner-key-row-${k.key}`}
      className={`border ${tone.split(" ").slice(1).join(" ")} bg-[#0E0E0E] p-3`}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-1 inline-block w-2 h-2 ${dot} shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-[0.2em] font-mono ${tone.split(" ")[0]}`}>
              {k.category}
            </span>
            <span className="font-mono text-white text-[13px] tracking-tight">
              {k.label}
            </span>
            <span
              className={
                "text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 " +
                (k.configured
                  ? "border border-emerald-500/40 text-emerald-300"
                  : k.required
                  ? "border border-rose-500/40 text-rose-300"
                  : "border border-zinc-700 text-zinc-400")
              }
            >
              {statusLabel}
            </span>
          </div>
          <p className="mt-1 text-[11.5px] text-zinc-400 leading-relaxed">
            {k.purpose}
          </p>
          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
            <a
              href={k.signup_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`owner-key-link-${k.key}`}
              className="text-[10.5px] font-mono uppercase tracking-wider text-amber-300 hover:text-white inline-flex items-center gap-1"
            >
              <ExternalLink size={10} /> {k.signup_label}
            </a>
            {k.masked && (
              <span className="text-[10px] font-mono text-zinc-500">
                Current:{" "}
                <span className="text-zinc-300 tabnum">{k.masked}</span>
              </span>
            )}
          </div>

          {editing ? (
            <div className="mt-2 flex items-center gap-2">
              <input
                data-testid={`owner-key-input-${k.key}`}
                type="password"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={`Paste ${k.label}…`}
                autoFocus
                className="flex-1 h-8 px-2 bg-[#0A0A0A] border border-[#27272A] focus:border-amber-400 outline-none text-[12px] font-mono"
              />
              <button
                data-testid={`owner-key-save-${k.key}`}
                onClick={() => save.mutate(draft.trim())}
                disabled={save.isPending || !draft.trim()}
                className="h-8 px-3 text-[10px] font-mono uppercase tracking-wider border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300 disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Check size={10} /> {save.isPending ? "SAVING…" : "SAVE"}
              </button>
              <button
                data-testid={`owner-key-cancel-${k.key}`}
                onClick={() => {
                  setEditing(false);
                  setDraft("");
                }}
                className="h-8 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-400 hover:text-white"
              >
                CANCEL
              </button>
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2">
              <button
                data-testid={`owner-key-edit-${k.key}`}
                onClick={() => setEditing(true)}
                className="h-8 px-3 text-[10px] font-mono uppercase tracking-wider border border-amber-400 text-amber-200 hover:bg-amber-400 hover:text-black inline-flex items-center gap-1"
              >
                <Key size={10} /> {k.configured ? "UPDATE" : "ADD_KEY"}
              </button>
              {k.configured && k.source === "db" && (
                <button
                  data-testid={`owner-key-clear-${k.key}`}
                  onClick={() => save.mutate("")}
                  className="h-8 px-2 text-[10px] font-mono uppercase tracking-wider border border-rose-500/40 text-rose-300 hover:bg-rose-400 hover:text-black inline-flex items-center gap-1"
                >
                  <Trash2 size={10} /> CLEAR
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function KeysManager() {
  const { data, isLoading } = useQuery({
    queryKey: ["owner-keys"],
    queryFn: () => auth.get("/owner/keys").then((r) => r.data),
  });
  const keys = data?.keys || [];
  const configured = keys.filter((k) => k.configured).length;
  const required_missing = keys.filter((k) => k.required && !k.configured).length;

  return (
    <section
      data-testid="owner-keys-manager"
      className="border border-[#27272A] bg-[#0E0E0E]"
    >
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[#27272A] flex-wrap">
        <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300">
          <Lock size={11} /> API_KEYS · SETUP
        </span>
        <span className="text-[11px] text-zinc-400 font-mono">
          {configured} / {keys.length} configured
          {required_missing > 0 && (
            <span className="ml-2 text-rose-300 inline-flex items-center gap-1">
              <ShieldAlert size={10} />
              {required_missing} REQUIRED MISSING
            </span>
          )}
        </span>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1">
          <Server size={10} /> stored in DB · env fallback
        </span>
      </header>

      <div className="p-3 space-y-2">
        {isLoading ? (
          <div className="text-center text-zinc-500 font-mono text-[11px] py-4">
            LOADING_KEYS…
          </div>
        ) : (
          keys.map((k) => <KeyRow key={k.key} k={k} />)
        )}
        <div className="text-[11px] text-zinc-500 font-mono leading-relaxed border-t border-[#27272A] pt-3 mt-3">
          Keys are stored in the <span className="text-zinc-300">fde_secrets</span>{" "}
          Mongo collection. Updates take effect immediately — no restart needed.
          DB values override the <span className="text-zinc-300">.env</span> defaults; CLEAR reverts to the env value (or unset if no env).
        </div>
      </div>
    </section>
  );
}
